/**
 * Format: Collection
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API collection responses.
 *
 */
import { formatFilters } from '~/format/format-filters'
import { formatImage } from '~/format/format-image'
import { formatProduct } from '~/format/format-product'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Format collection.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Collection
 * @param {Object} response - Collection response.
 * @returns {Boolean|Object}
 */
export function formatCollection(response, { currentPage = 1, currentOffset = 0 }) {
  const collection = response.collection

  if (!collection) {
    return false
  }

  const formattedCollection = {
    description: collection?.descriptionHtml,
    handle: collection.handle,
    id: decodeApiId(collection.id),
    pageInfo: {
      ...collection.products?.pageInfo,
      currentOffset,
      currentPage,
      liquid: false,
    },
    title: collection.title,
    url: `//${location.host}${cnvs.routes.normalisedRoot}collections/${collection.handle}`,
  }

  /**
   * Format filters.
   */
  if (collection.products) {
    formattedCollection.filters = Object.values(collection.products?.filters)

    /**
     * Get total product count using vendors filter.
     */
    const vendorFilter = formattedCollection.filters?.find((filter) => {
      return filter.id === 'filter.p.vendor'
    })

    if (vendorFilter) {
      const totalProducts = vendorFilter.values?.reduce((a, b) => {
        return {
          count: a.count + b.count,
        }
      })

      formattedCollection.pageInfo.totalCount = totalProducts.count || 0
    }

    formattedCollection.filters = formatFilters(
      formattedCollection.filters,
      formattedCollection.handle,
    )
  }

  /**
   * Format products.
   */
  formattedCollection.products = transformEdges(collection.products).map((product) => {
    return formatProduct(product)
  })

  if (collection.image) {
    formattedCollection.image = formatImage(collection.image)
  }

  return formattedCollection
}
